'use client';

import { useState, useCallback } from 'react';
import { Share2 } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { posthog } from '@/lib/analytics/posthog';
import ShareSheet from './ShareSheet';
import { Button } from '@/components/ui/button';

interface ShareButtonProps {
  /** The URL to share (will be shortened) */
  url: string;
  /** Title for the share dialog */
  title: string;
  /** Text description for the share dialog */
  text?: string;
  /** Optional content ID to associate with the short link */
  contentId?: string;
  /** Optional title for the short link (for organizer reference) */
  linkTitle?: string;
  /** Button size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Button style variant */
  variant?: 'default' | 'outline' | 'ghost';
  /** Additional CSS classes */
  className?: string;
  /** Show label text */
  showLabel?: boolean;
}

export default function ShareButton({
  url,
  title,
  text,
  contentId,
  linkTitle,
  size = 'md',
  variant = 'default',
  className = '',
  showLabel = true
}: ShareButtonProps) {
  const { t } = useLanguage();
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);

  const iconSize = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  // Try native Web Share API first (mobile), fallback to custom sheet
  const handleShare = useCallback(async () => {
    // Check if native share is available and likely on mobile
    if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title,
          text: text || title,
          url
        });
        // Track successful native share
        posthog?.capture('content_shared', {
          platform: 'native',
          content_id: contentId,
          share_url: url
        });
        return;
      } catch (error) {
        // User cancelled or share failed - fall through to custom sheet
        if ((error as Error).name === 'AbortError') {
          return; // User cancelled, don't show fallback
        }
      }
    }

    // Fallback: show custom share sheet
    setIsShareSheetOpen(true);
  }, [title, text, url, contentId]);

  const buttonVariant = variant === 'default' ? 'primary' : variant === 'outline' ? 'outline' : 'ghost';
  const buttonSize = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default';

  return (
    <>
      <Button
        onClick={handleShare}
        variant={buttonVariant}
        size={buttonSize}
        className={`${showLabel ? '' : 'px-2'} ${className}`}
        aria-label={t('common.share') || 'Share'}
      >
        <Share2 className={iconSize[size]} />
        {showLabel && (
          <span>{t('common.share') || 'Share'}</span>
        )}
      </Button>

      <ShareSheet
        isOpen={isShareSheetOpen}
        onClose={() => setIsShareSheetOpen(false)}
        url={url}
        title={title}
        text={text}
        contentId={contentId}
        linkTitle={linkTitle}
      />
    </>
  );
}
